import WaterProofIcon from "./features-icons/waterproof.svg";
import LiningIcon from "./features-icons/lining.svg";
import SnowIcon from "./features-icons/snow.svg";
import SunIcon from "./features-icons/sun.svg";
import DurabilityIcon from "./features-icons/durability.svg";
import BreathabilityIcon from "./features-icons/breathability.svg";
import TraileringIcon from "./features-icons/trailering.svg";
import SpeedIcon from "./features-icons/speed.svg";
import DustIcon from "./features-icons/dust.svg";
import StorageIcon from "./features-icons/storage.svg";
import SupportIcon from "./features-icons/support.svg";

/**
 * Determines the product type based on the product title
 * @param {string} title - Product title
 * @returns {string} Product type: 'car', 'boat', 'bimini', 'motorsport', or 'car' (default)
 */
export const getProductType = (title) => {
    if (title?.toLowerCase().includes('car')) return 'car';
    if (title?.toLowerCase().includes('suv')) return 'car';
    if (title?.toLowerCase().includes('van')) return 'car';
    if (title?.toLowerCase().includes('truck')) return 'car';
    if (title?.toLowerCase().includes('boat')) return 'boat';
    if (title?.toLowerCase().includes('jetski')) return 'boat';
    if (title?.toLowerCase().includes('bimini')) return 'bimini';
    if (title?.toLowerCase().includes('pontoon')) return 'boat';
    if (title?.toLowerCase().includes('motorcycle')) return 'motorsport';
    if (title?.toLowerCase().includes('scooter')) return 'motorsport';
    if (title?.toLowerCase().includes('atv')) return 'motorsport';
    if (title?.toLowerCase().includes('utv')) return 'motorsport';
    if (title?.toLowerCase().includes('snowmobile')) return 'motorsport';
    if (title?.toLowerCase().includes('patio')) return 'patio';
    return 'car';
};

/**
 * Determines the product tier based on the product title
 * @param {string} title - Product title
 * @returns {string} Product tier: 'supreme', 'elite', 'fiveLayer', 'proguard', 'westCoast', 'hail', or 'supreme' (default)
 */
export const getProductTier = (title) => {
    if (title?.toLowerCase().includes('hail')) return 'hail';
    if (title?.toLowerCase().includes('supreme')) return 'supreme';
    if (title?.toLowerCase().includes('elite')) return 'elite';
    if (title?.toLowerCase().includes('5 layer')) return 'fiveLayer';
    if (title?.toLowerCase().includes('proguard')) return 'proguard';
    if (title?.toLowerCase().includes('west coast')) return 'westCoast';
    return 'supreme';
};

/**
 * Feature definitions for Supreme tier products
 */
export const supremeFeatures = {
    waterproof: {
        icon: WaterProofIcon,
        value: 5,
        label: "Water Proof Protection"
    },
    trailering: {
        icon: TraileringIcon,
        value: 5,
        label: "Trailering"
    },
    speedRated: {
        icon: SpeedIcon,
        value: 5,
        label: "Speed Rated"
    },
    softness: {
        icon: LiningIcon,
        value: 5,
        label: "Inner Lining Softness"
    },
    snowProtection: {
        icon: SnowIcon,
        value: 5,
        label: "Snow/Hail/Ice Protection"
    },
    sun: {
        icon: SunIcon,
        value: 5,
        label: "Sun/UV Protection"
    },
    durability: {
        icon: DurabilityIcon,
        value: 5,
        label: "Durability/Thickness"
    },
    breathability: {
        icon: BreathabilityIcon,
        value: 5,
        label: "Breathability"
    },
    storage: {
        icon: StorageIcon,
        value: 5,
        label: "Storage Boat"
    },
    supportPole: {
        icon: SupportIcon,
        value: 5,
        label: "Support Pole"
    }
};

/**
 * Feature definitions for Elite tier products
 */
export const eliteFeatures = {
    waterproof: {
        icon: WaterProofIcon,
        value: 4,
        label: "Water Proof Protection"
    },
    trailering: {
        icon: TraileringIcon,
        value: 3,
        label: "Trailering"
    },
    speedRated: {
        icon: SpeedIcon,
        value: 3,
        label: "Speed Rated"
    },
    softness: {
        icon: LiningIcon,
        value: 3,
        label: "Inner Lining Softness"
    },
    snowProtection: {
        icon: SnowIcon,
        value: 4,
        label: "Snow/Hail/Ice Protection"
    },
    sun: {
        icon: SunIcon,
        value: 3,
        label: "Sun/UV Protection"
    },
    durability: {
        icon: DurabilityIcon,
        value: 4,
        label: "Durability/Thickness"
    },
    breathability: {
        icon: BreathabilityIcon,
        value: 3,
        label: "Breathability"
    },
    storage: {
        icon: StorageIcon,
        value: 5,
        label: "Storage Boat"
    },
    supportPole: {
        icon: SupportIcon,
        value: 5,
        label: "Support Pole"
    }
};

/**
 * Feature definitions for Five Layer tier products
 */
export const fiveLayerFeatures = {
    waterproof: {
        icon: WaterProofIcon,
        value: 3,
        label: "Water Proof Protection"
    },
    trailering: {
        icon: TraileringIcon,
        value: 3,
        label: "Trailering"
    },
    speedRated: {
        icon: SpeedIcon,
        value: 3,
        label: "Speed Rated"
    },
    softness: {
        icon: LiningIcon,
        value: 2,
        label: "Inner Lining Softness"
    },
    snowProtection: {
        icon: SnowIcon,
        value: 3,
        label: "Snow/Hail/Ice Protection"
    },
    sun: {
        icon: SunIcon,
        value: 3,
        label: "Sun/UV Protection"
    },
    durability: {
        icon: DurabilityIcon,
        value: 3,
        label: "Durability/Thickness"
    },
    breathability: {
        icon: BreathabilityIcon,
        value: 1,
        label: "Breathability"
    }
};

/**
 * Feature definitions for Hail tier products
 */
export const hailFeatures = {
    snowProtection: {
        icon: SnowIcon,
        value: 5,
        label: "Snow/Hail/Ice Protection"
    },
    waterproof: {
        icon: WaterProofIcon,
        value: 3,
        label: "Water Proof Protection"
    },
    softness: {
        icon: LiningIcon,
        value: 3,
        label: "Inner Lining Softness"
    },
    sun: {
        icon: SunIcon,
        value: 3,
        label: "Sun/UV Protection"
    },
    durability: {
        icon: DurabilityIcon,
        value: 5,
        label: "Durability/Thickness"
    },
    breathability: {
        icon: BreathabilityIcon,
        value: 4,
        label: "Breathability"
    }
};

/**
 * Feature definitions for ProGuard tier products
 */
export const proguardFeatures = {
    dust: {
        icon: DustIcon,
        value: 5,
        label: "Dust Proof Protection"
    },
    waterproof: {
        icon: WaterProofIcon,
        value: 3,
        label: "Water Proof Protection"
    },
    trailering: {
        icon: TraileringIcon,
        value: 3,
        label: "Trailering"
    },
    speedRated: {
        icon: SpeedIcon,
        value: 3,
        label: "Speed Rated"
    },
    softness: {
        icon: LiningIcon,
        value: 1,
        label: "Inner Lining Softness"
    },
    snowProtection: {
        icon: SnowIcon,
        value: 2,
        label: "Snow/Hail/Ice Protection"
    },
    sun: {
        icon: SunIcon,
        value: 4,
        label: "Sun/UV Protection"
    },
    durability: {
        icon: DurabilityIcon,
        value: 3,
        label: "Durability/Thickness"
    },
    breathability: {
        icon: BreathabilityIcon,
        value: 3,
        label: "Breathability"
    },
    storage: {
        icon: StorageIcon,
        value: 5,
        label: "Storage Boat"
    },
    supportPole: {
        icon: SupportIcon,
        value: 5,
        label: "Support Pole"
    }
};

/**
 * Features configuration organized by product type and tier
 */
export const features = {
    car: {
        westCoast: [
            supremeFeatures.waterproof,
            supremeFeatures.softness,
            supremeFeatures.snowProtection,
            supremeFeatures.sun,
            supremeFeatures.durability,
            supremeFeatures.breathability,
        ],
        supreme: [
            supremeFeatures.waterproof,
            supremeFeatures.softness,
            supremeFeatures.snowProtection,
            supremeFeatures.sun,
            supremeFeatures.durability,
            supremeFeatures.breathability,
        ],
        elite: [
            eliteFeatures.waterproof,
            eliteFeatures.softness,
            eliteFeatures.snowProtection,
            eliteFeatures.sun,
            eliteFeatures.durability,
            eliteFeatures.breathability,
        ],
        fiveLayer: [
            fiveLayerFeatures.waterproof,
            fiveLayerFeatures.softness,
            fiveLayerFeatures.snowProtection,
            fiveLayerFeatures.sun,
            fiveLayerFeatures.durability,
            fiveLayerFeatures.breathability,
        ],
        proguard: [
            proguardFeatures.dust,
            proguardFeatures.softness,
            proguardFeatures.snowProtection,
            proguardFeatures.sun,
            proguardFeatures.durability,
            proguardFeatures.breathability,
        ],
        hail: [
            hailFeatures.waterproof,
            hailFeatures.softness,
            hailFeatures.snowProtection,
            hailFeatures.sun,
            hailFeatures.durability,
            hailFeatures.breathability,
        ]
    },
    patio: {
        westCoast: [
            supremeFeatures.waterproof,
            // supremeFeatures.softness,
            // supremeFeatures.snowProtection,
            supremeFeatures.sun,
            supremeFeatures.durability,
            supremeFeatures.breathability,
        ],
        supreme: [
            supremeFeatures.waterproof,
            // supremeFeatures.softness,
            // supremeFeatures.snowProtection,
            supremeFeatures.sun,
            supremeFeatures.durability,
            supremeFeatures.breathability,
        ],
        elite: [
            eliteFeatures.waterproof,
            // eliteFeatures.softness,
            // eliteFeatures.snowProtection,
            eliteFeatures.sun,
            eliteFeatures.durability,
            eliteFeatures.breathability,
        ],
        fiveLayer: [
            fiveLayerFeatures.waterproof,
            // fiveLayerFeatures.softness,
            // fiveLayerFeatures.snowProtection,
            fiveLayerFeatures.sun,
            fiveLayerFeatures.durability,
            fiveLayerFeatures.breathability,
        ],
        proguard: [
            proguardFeatures.waterproof,
            proguardFeatures.sun,
            proguardFeatures.durability,
            proguardFeatures.breathability,
        ]
    },
    motorsport: {
        supreme: [
            supremeFeatures.waterproof,
            supremeFeatures.speedRated,
            supremeFeatures.snowProtection,
            supremeFeatures.sun,
            supremeFeatures.durability,
            supremeFeatures.breathability,
        ],
        elite: [
            eliteFeatures.waterproof,
            eliteFeatures.speedRated,
            eliteFeatures.snowProtection,
            eliteFeatures.sun,
            eliteFeatures.durability,
            eliteFeatures.breathability,
        ],
        proguard: [
            proguardFeatures.dust,
            proguardFeatures.speedRated,
            proguardFeatures.snowProtection,
            proguardFeatures.sun,
            proguardFeatures.durability,
            proguardFeatures.breathability,
        ]
    },
    bimini: {
        supreme: [
            supremeFeatures.waterproof,
            supremeFeatures.storage,
            supremeFeatures.sun,
            supremeFeatures.speedRated,
            supremeFeatures.durability,
            supremeFeatures.supportPole,
        ],
        elite: [
            eliteFeatures.waterproof,
            eliteFeatures.storage,
            eliteFeatures.speedRated,
            eliteFeatures.sun,
            eliteFeatures.durability,
            eliteFeatures.supportPole,
        ],
        proguard: [
            proguardFeatures.dust,
            proguardFeatures.storage,
            proguardFeatures.speedRated,
            proguardFeatures.sun,
            proguardFeatures.durability,
            proguardFeatures.supportPole,
        ]
    },
    boat: {
        supreme: [
            supremeFeatures.waterproof,
            supremeFeatures.trailering,
            supremeFeatures.snowProtection,
            supremeFeatures.sun,
            supremeFeatures.durability,
            supremeFeatures.breathability,
        ],
        elite: [
            eliteFeatures.waterproof,
            eliteFeatures.trailering,
            eliteFeatures.snowProtection,
            eliteFeatures.sun,
            eliteFeatures.durability,
            eliteFeatures.breathability,
        ]
    }
};

